// Mock Authentication Service for Development/Demo
// This simulates Firebase Auth without requiring a real Firebase project

const MOCK_USERS = [
  {
    uid: 'admin-001',
    email: 'admin@agendapro.com',
    password: 'admin123',
    name: 'Administrador',
    role: 'clinicAdmin',
    phone: '(11) 99999-0001',
    specialty: '',
    active: true,
    avatarColor: 'red',
  },
  {
    uid: 'pro-001',
    email: 'pro@agendapro.com',
    password: 'pro123',
    name: 'Dr. João Silva',
    role: 'professional',
    phone: '(11) 99999-0002',
    specialty: 'Dermatologia',
    active: true,
    avatarColor: 'blue',
    photo: null,
  },
  {
    uid: 'pro-002',
    email: 'mariana@agendapro.com',
    password: 'pro123',
    name: 'Mariana Silva',
    role: 'professional',
    phone: '(11) 98432-1100',
    specialty: 'Esteticista facial',
    active: true,
    avatarColor: 'rose',
    photo: null,
  },
  {
    uid: 'pro-003',
    email: 'camila@agendapro.com',
    password: 'pro123',
    name: 'Camila Andrade',
    role: 'professional',
    phone: '(11) 99544-2200',
    specialty: 'Massoterapeuta',
    active: true,
    avatarColor: 'mint',
    photo: null,
  },
  {
    uid: 'pro-004',
    email: 'laura@agendapro.com',
    password: 'pro123',
    name: 'Laura Pires',
    role: 'professional',
    phone: '(11) 97123-3344',
    specialty: 'Manicure & nail-art',
    active: true,
    avatarColor: 'peach',
    photo: null,
  },
  {
    uid: 'pro-005',
    email: 'vanessa@agendapro.com',
    password: 'pro123',
    name: 'Vanessa Lima',
    role: 'professional',
    phone: '(11) 99077-5566',
    specialty: 'Esteticista avançada',
    active: true,
    avatarColor: 'lavender',
    photo: null,
  },
  {
    uid: 'client-001',
    email: 'cliente@agendapro.com',
    password: 'cliente123',
    name: 'Maria Santos',
    role: 'client',
    phone: '(11) 99999-0003',
    specialty: '',
    active: true,
    avatarColor: 'green',
  },
  {
    uid: 'client-002',
    email: 'marina@email.com',
    password: 'cliente123',
    name: 'Marina Ribeiro',
    role: 'client',
    phone: '(11) 98432-1100',
    specialty: '',
    active: true,
    avatarColor: 'blue',
  },
  {
    uid: 'client-003',
    email: 'pedro@email.com',
    password: 'cliente123',
    name: 'Pedro Almeida',
    role: 'client',
    phone: '(11) 99213-4421',
    specialty: '',
    active: true,
    avatarColor: 'lavender',
  },
  {
    uid: 'client-004',
    email: 'julia@email.com',
    password: 'cliente123',
    name: 'Júlia Costa',
    role: 'client',
    phone: '(11) 97755-3120',
    specialty: '',
    active: true,
    avatarColor: 'rose',
  },
];

// Mock in-memory database
let currentUser = null;
let authListeners = [];

// Simulate Firebase Auth User object
const createAuthUser = (mockUser) => ({
  uid: mockUser.uid,
  email: mockUser.email,
  displayName: mockUser.name,
});

// Simulate delay like real API
const delay = (ms = 300) => new Promise(resolve => setTimeout(resolve, ms));

export const mockAuthService = {
  onAuthChange: (callback) => {
    authListeners.push(callback);
    // Immediately call with current state
    callback(currentUser);
    // Return unsubscribe function
    return () => {
      authListeners = authListeners.filter(cb => cb !== callback);
    };
  },

  login: async (email, password) => {
    await delay();
    const user = MOCK_USERS.find(u => u.email === email && u.password === password);
    if (!user) {
      throw new Error('Invalid credentials');
    }
    currentUser = createAuthUser(user);
    // Notify all listeners
    authListeners.forEach(cb => cb(currentUser));
    return { user: currentUser };
  },

  logout: async () => {
    await delay(100);
    currentUser = null;
    // Notify all listeners
    authListeners.forEach(cb => cb(null));
  },

  resetPassword: async (email) => {
    await delay();
    const user = MOCK_USERS.find(u => u.email === email);
    if (!user) {
      throw new Error('User not found');
    }
    console.log(`[Mock] Password reset email sent to ${email}`);
  },

  register: async ({ email, password, name, role, phone = '', specialty = '' }) => {
    await delay();
    const newUser = {
      uid: `user-${Date.now()}`,
      email,
      name,
      role,
      phone,
      specialty,
      active: true,
      avatarColor: ['red', 'blue', 'green', 'gold', 'purple'][Math.floor(Math.random() * 5)],
    };
    MOCK_USERS.push({ ...newUser, password });
    return createAuthUser(newUser);
  },
};

export const mockUsersService = {
  create: async (uid, data) => {
    await delay(100);
    console.log('[Mock] User created:', uid, data);
  },

  getById: async (uid) => {
    await delay(100);
    const user = MOCK_USERS.find(u => u.uid === uid);
    if (!user) return null;
    const { password, ...userWithoutPassword } = user;
    return { id: uid, ...userWithoutPassword };
  },

  update: async (uid, data) => {
    await delay(100);
    const userIndex = MOCK_USERS.findIndex(u => u.uid === uid);
    if (userIndex !== -1) {
      MOCK_USERS[userIndex] = { ...MOCK_USERS[userIndex], ...data };
    }
  },

  delete: async (uid) => {
    await delay(100);
    const index = MOCK_USERS.findIndex(u => u.uid === uid);
    if (index !== -1) {
      MOCK_USERS.splice(index, 1);
    }
  },

  onSnapshot: (callback, role) => {
    const filteredUsers = role
      ? MOCK_USERS.filter(u => u.role === role)
      : MOCK_USERS;
    
    const usersWithoutPasswords = filteredUsers.map(({ password, ...user }) => ({
      id: user.uid,
      ...user,
    }));
    
    // Call immediately with current data
    setTimeout(() => callback(usersWithoutPasswords), 100);
    
    // Return unsubscribe function
    return () => {};
  },

  getByRole: async (role) => {
    await delay(100);
    const filteredUsers = MOCK_USERS.filter(u => u.role === role);
    return filteredUsers.map(({ password, ...user }) => ({
      id: user.uid,
      ...user,
    }));
  },
};

// Mock data for procedures
let mockProcedures = [
  {
    id: 'proc-001',
    name: 'Limpeza de Pele',
    duration: 50,
    price: 180,
    category: 'Estética facial',
    emoji: '💆',
    photo: null,
    description: 'Higienização profunda do rosto com extração de cravos, esfoliação e máscara calmante. Ideal para pele oleosa.',
    professionals: ['pro-002', 'pro-005'], // Mariana e Vanessa
    approved: true,
    createdAt: new Date(),
  },
  {
    id: 'proc-002',
    name: 'Massagem Relaxante',
    duration: 60,
    price: 140,
    category: 'Estética corporal',
    emoji: '💆‍♀️',
    photo: null,
    description: 'Massagem corporal com movimentos suaves para alívio de tensão muscular e melhora da circulação.',
    professionals: ['pro-003'], // Camila
    approved: true,
    createdAt: new Date(),
  },
  {
    id: 'proc-003',
    name: 'Manicure + Pedicure',
    duration: 90,
    price: 95,
    category: 'Estética',
    emoji: '💅',
    photo: null,
    description: 'Cuidado completo das unhas: cutícula, esfoliação, hidratação e esmaltação à sua escolha.',
    professionals: ['pro-004'], // Laura
    approved: true,
    createdAt: new Date(),
  },
  {
    id: 'proc-004',
    name: 'Microagulhamento',
    duration: 60,
    price: 350,
    category: 'Estética avançada',
    emoji: '✨',
    photo: null,
    description: 'Tratamento avançado de indução de colágeno para uniformização da pele e atenuação de cicatrizes.',
    professionals: ['pro-005'], // Vanessa
    approved: false,
    createdAt: new Date(),
  },
  {
    id: 'proc-005',
    name: 'Drenagem Linfática',
    duration: 60,
    price: 160,
    category: 'Estética corporal',
    emoji: '💆',
    photo: null,
    description: 'Massagem terapêutica que estimula o sistema linfático, reduzindo retenção de líquido e inchaço.',
    professionals: ['pro-003', 'pro-005'], // Camila e Vanessa
    approved: true,
    createdAt: new Date(),
  },
  {
    id: 'proc-006',
    name: 'Peeling Químico',
    duration: 45,
    price: 220,
    category: 'Estética facial',
    emoji: '✨',
    photo: null,
    description: 'Renovação celular profunda para tratar manchas, acne e melhorar textura da pele.',
    professionals: ['pro-001', 'pro-002'], // João e Mariana
    approved: true,
    createdAt: new Date(),
  },
];

export const mockProceduresService = {
  add: async (data) => {
    await delay(100);
    const newProc = {
      id: `proc-${Date.now()}`,
      ...data,
      approved: data.approved ?? false,
      createdAt: new Date(),
    };
    mockProcedures.push(newProc);
    return { id: newProc.id };
  },

  create: async (data) => mockProceduresService.add(data),

  update: async (id, data) => {
    await delay(100);
    const index = mockProcedures.findIndex(p => p.id === id);
    if (index !== -1) {
      mockProcedures[index] = { ...mockProcedures[index], ...data };
    }
  },

  delete: async (id) => {
    await delay(100);
    mockProcedures = mockProcedures.filter(p => p.id !== id);
  },

  reject: async (id) => mockProceduresService.delete(id),

  approve: async (id) => {
    await delay(100);
    const proc = mockProcedures.find(p => p.id === id);
    if (proc) {
      proc.approved = true;
      proc.approvedAt = new Date();
    }
  },

  onSnapshot: (callback) => {
    setTimeout(() => callback(mockProcedures), 100);
    return () => {};
  },
};

// Mock appointments
let mockAppointments = [
  {
    id: 'appt-001',
    clientId: 'client-001',
    clientName: 'Maria Santos',
    professionalId: 'pro-002',
    professionalName: 'Mariana Silva',
    procedureId: 'proc-001',
    procedureName: 'Limpeza de Pele',
    date: new Date().toISOString().split('T')[0],
    time: '14:00',
    status: 'confirmed',
    done: false,
    price: 180,
    duration: 50,
    createdAt: new Date(),
  },
  {
    id: 'appt-002',
    clientId: 'client-002',
    clientName: 'Marina Ribeiro',
    professionalId: 'pro-003',
    professionalName: 'Camila Andrade',
    procedureId: 'proc-002',
    procedureName: 'Massagem Relaxante',
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 2);
      return d.toISOString().split('T')[0];
    })(),
    time: '10:00',
    status: 'pending',
    done: false,
    price: 140,
    duration: 60,
    createdAt: new Date(),
  },
  {
    id: 'appt-003',
    clientId: 'client-003',
    clientName: 'Pedro Almeida',
    professionalId: 'pro-004',
    professionalName: 'Laura Pires',
    procedureId: 'proc-003',
    procedureName: 'Manicure + Pedicure',
    date: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 3);
      return d.toISOString().split('T')[0];
    })(),
    time: '15:30',
    status: 'pending',
    done: false,
    price: 95,
    duration: 90,
    createdAt: new Date(),
  },
];

export const mockAppointmentsService = {
  add: async (data) => {
    await delay(100);
    const newAppt = {
      id: `appt-${Date.now()}`,
      ...data,
      status: 'waitingConfirm',
      done: false,
      createdAt: new Date(),
    };
    mockAppointments.push(newAppt);
    // Trigger listeners
    mockAppointmentsService._triggerListeners();
    return { id: newAppt.id };
  },

  update: async (id, data) => {
    await delay(100);
    const index = mockAppointments.findIndex(a => a.id === id);
    if (index !== -1) {
      mockAppointments[index] = { ...mockAppointments[index], ...data };
      // Trigger listeners
      mockAppointmentsService._triggerListeners();
    }
  },

  delete: async (id) => {
    await delay(100);
    mockAppointments = mockAppointments.filter(a => a.id !== id);
    // Trigger listeners
    mockAppointmentsService._triggerListeners();
  },

  confirm: async (id) => {
    await delay(100);
    const appt = mockAppointments.find(a => a.id === id);
    if (appt) {
      appt.status = 'confirmed';
      // Trigger listeners
      mockAppointmentsService._triggerListeners();
    }
  },

  cancel: async (id) => {
    await delay(100);
    const appt = mockAppointments.find(a => a.id === id);
    if (appt) {
      appt.status = 'cancelled';
      appt.cancelledAt = new Date();
      // Trigger listeners
      mockAppointmentsService._triggerListeners();
    }
  },

  markDone: async (id) => {
    await delay(100);
    const appt = mockAppointments.find(a => a.id === id);
    if (appt) {
      appt.done = true;
      appt.doneAt = new Date();
    }
  },

  setWaitingConfirm: async (id) => {
    await delay(100);
    const appt = mockAppointments.find(a => a.id === id);
    if (appt) appt.status = 'waitingConfirm';
  },

  onSnapshot: (callback, filters = {}) => {
    // Initialize listeners array if not exists
    if (!mockAppointmentsService._listeners) {
      mockAppointmentsService._listeners = [];
    }
    
    // Add this listener
    const listener = { callback, filters };
    mockAppointmentsService._listeners.push(listener);
    
    // Apply filters and call immediately
    let filtered = [...mockAppointments];
    
    if (filters.professionalId) {
      filtered = filtered.filter(a => a.professionalId === filters.professionalId);
    }
    if (filters.clientId) {
      filtered = filtered.filter(a => a.clientId === filters.clientId);
    }
    if (filters.date) {
      filtered = filtered.filter(a => a.date === filters.date);
    }
    if (filters.status) {
      filtered = filtered.filter(a => a.status === filters.status);
    }
    
    setTimeout(() => callback(filtered), 100);
    
    // Return unsubscribe function
    return () => {
      const index = mockAppointmentsService._listeners.indexOf(listener);
      if (index > -1) {
        mockAppointmentsService._listeners.splice(index, 1);
      }
    };
  },

  _triggerListeners: () => {
    if (!mockAppointmentsService._listeners) return;
    
    mockAppointmentsService._listeners.forEach(listener => {
      let filtered = [...mockAppointments];
      
      if (listener.filters.professionalId) {
        filtered = filtered.filter(a => a.professionalId === listener.filters.professionalId);
      }
      if (listener.filters.clientId) {
        filtered = filtered.filter(a => a.clientId === listener.filters.clientId);
      }
      if (listener.filters.date) {
        filtered = filtered.filter(a => a.date === listener.filters.date);
      }
      if (listener.filters.status) {
        filtered = filtered.filter(a => a.status === listener.filters.status);
      }
      
      listener.callback(filtered);
    });
  },

  getOccupiedSlots: async (professionalId, date) => {
    await delay(100);
    return mockAppointments
      .filter(a => 
        a.professionalId === professionalId &&
        a.date === date &&
        ['pending', 'confirmed', 'waitingConfirm'].includes(a.status)
      )
      .map(a => a.time);
  },
};

let mockNotifications = [
  {
    id: 'notif-001',
    userId: 'client-001',
    type: 'confirm',
    title: 'Confirme seu agendamento',
    message: 'Seu horário foi agendado: Limpeza de Pele com Mariana Silva. Confirme sua presença com pelo menos 5h de antecedência.',
    appointmentId: 'appt-001',
    appointmentDate: new Date().toISOString().split('T')[0],
    appointmentTime: '14:00',
    read: false,
    createdAt: new Date(),
  },
  {
    id: 'notif-002',
    userId: 'client-002',
    type: 'confirm',
    title: 'Confirme seu agendamento',
    message: 'Você tem um agendamento de Massagem Relaxante com Camila Andrade. Não esqueça de confirmar sua presença!',
    appointmentId: 'appt-002',
    appointmentDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() + 2);
      return d.toISOString().split('T')[0];
    })(),
    appointmentTime: '10:00',
    read: false,
    createdAt: new Date(),
  },
];

export const mockNotificationsService = {
  add: async (data) => {
    await delay(100);
    const notif = {
      id: `notif-${Date.now()}`,
      ...data,
      read: false,
      createdAt: new Date(),
    };
    mockNotifications.push(notif);
    // Trigger listeners
    if (mockNotificationsService._listeners) {
      mockNotificationsService._listeners.forEach(listener => {
        if (listener.userId === data.userId) {
          const filtered = mockNotifications.filter(n => n.userId === data.userId && !n.read);
          listener.callback(filtered);
        }
      });
    }
    return { id: notif.id };
  },

  dismiss: async (id) => {
    await delay(100);
    const notif = mockNotifications.find(n => n.id === id);
    if (notif) {
      notif.read = true;
      notif.readAt = new Date();
      // Trigger listeners for this user
      if (mockNotificationsService._listeners) {
        mockNotificationsService._listeners.forEach(listener => {
          if (listener.userId === notif.userId) {
            const filtered = mockNotifications.filter(n => n.userId === notif.userId && !n.read);
            listener.callback(filtered);
          }
        });
      }
    }
  },

  onSnapshotByClient: (userId, callback) => {
    // Initialize listeners array if not exists
    if (!mockNotificationsService._listeners) {
      mockNotificationsService._listeners = [];
    }
    
    // Add this listener
    const listener = { userId, callback };
    mockNotificationsService._listeners.push(listener);
    
    // Call immediately with current data
    const filtered = mockNotifications.filter(n => n.userId === userId && !n.read);
    setTimeout(() => callback(filtered), 100);
    
    // Return unsubscribe function
    return () => {
      const index = mockNotificationsService._listeners.indexOf(listener);
      if (index > -1) {
        mockNotificationsService._listeners.splice(index, 1);
      }
    };
  },
};

let mockReschedules = [];

export const mockRescheduleService = {
  request: async (data) => {
    await delay(100);
    const req = {
      id: `rsch-${Date.now()}`,
      ...data,
      status: 'pending',
      createdAt: new Date(),
    };
    mockReschedules.push(req);
    return { id: req.id };
  },

  accept: async (id, appointmentId, newDate, newTime) => {
    await delay(100);
    const rsch = mockReschedules.find(r => r.id === id);
    if (rsch) {
      rsch.status = 'accepted';
      rsch.resolvedAt = new Date();
    }
    const appt = mockAppointments.find(a => a.id === appointmentId);
    if (appt) {
      appt.date = newDate;
      appt.time = newTime;
      appt.status = 'confirmed';
    }
  },

  reject: async (id) => {
    await delay(100);
    const rsch = mockReschedules.find(r => r.id === id);
    if (rsch) {
      rsch.status = 'rejected';
      rsch.resolvedAt = new Date();
    }
  },

  onSnapshotByClient: (clientId, callback) => {
    const filtered = mockReschedules.filter(r => r.clientId === clientId && r.status === 'pending');
    setTimeout(() => callback(filtered), 100);
    return () => {};
  },
};

export const mockServerTimestamp = () => new Date();

// ─── SCHEDULE WINDOWS ─────────────────────────────────────────────────────────
// Janelas de bloqueio: períodos em que um profissional está indisponível.
// status: 'pending' (criado pelo profissional, aguarda aprovação)
//         'approved' (bloqueia slots na agenda)
//         'rejected' (ignorado)

const tomorrow = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().split('T')[0]; })();
const in3days  = (() => { const d = new Date(); d.setDate(d.getDate() + 3); return d.toISOString().split('T')[0]; })();
const in5days  = (() => { const d = new Date(); d.setDate(d.getDate() + 5); return d.toISOString().split('T')[0]; })();

let mockWindows = [
  {
    id: 'win-001',
    professionalId: 'pro-002',
    professionalName: 'Mariana Silva',
    allProfessionals: false,
    date: tomorrow,
    startTime: '12:00',
    endTime: '14:00',
    reason: 'Intervalo para almoço',
    status: 'approved',
    createdBy: 'admin-001',
    createdByRole: 'admin',
    approvedBy: 'admin-001',
    approvedAt: new Date(),
    createdAt: new Date(),
  },
  {
    id: 'win-002',
    professionalId: 'pro-003',
    professionalName: 'Camila Andrade',
    allProfessionals: false,
    date: in3days,
    startTime: '08:00',
    endTime: '10:00',
    reason: 'Curso de especialização',
    status: 'pending',
    createdBy: 'pro-003',
    createdByRole: 'professional',
    approvedBy: null,
    approvedAt: null,
    createdAt: new Date(),
  },
  {
    id: 'win-003',
    professionalId: null,
    professionalName: null,
    allProfessionals: true,
    date: in5days,
    startTime: '13:00',
    endTime: '15:00',
    reason: 'Reunião de equipe',
    status: 'approved',
    createdBy: 'admin-001',
    createdByRole: 'admin',
    approvedBy: 'admin-001',
    approvedAt: new Date(),
    createdAt: new Date(),
  },
];

export const mockScheduleWindowsService = {
  _listeners: [],

  _trigger() {
    this._listeners.forEach(l => l([...mockWindows]));
  },

  add: async (data) => {
    await delay(100);
    const win = {
      id: `win-${Date.now()}`,
      ...data,
      createdAt: new Date(),
    };
    mockWindows.push(win);
    mockScheduleWindowsService._trigger();
    return { id: win.id };
  },

  update: async (id, data) => {
    await delay(100);
    const idx = mockWindows.findIndex(w => w.id === id);
    if (idx !== -1) mockWindows[idx] = { ...mockWindows[idx], ...data };
    mockScheduleWindowsService._trigger();
  },

  delete: async (id) => {
    await delay(100);
    mockWindows = mockWindows.filter(w => w.id !== id);
    mockScheduleWindowsService._trigger();
  },

  approve: async (id, adminId) => {
    await delay(100);
    const win = mockWindows.find(w => w.id === id);
    if (win) {
      win.status = 'approved';
      win.approvedBy = adminId;
      win.approvedAt = new Date();
    }
    mockScheduleWindowsService._trigger();
    // Disparar atualização dos agendamentos pois slots mudam
    mockAppointmentsService._triggerListeners();
  },

  reject: async (id) => {
    await delay(100);
    const win = mockWindows.find(w => w.id === id);
    if (win) win.status = 'rejected';
    mockScheduleWindowsService._trigger();
    mockAppointmentsService._triggerListeners();
  },

  onSnapshot: (callback) => {
    mockScheduleWindowsService._listeners.push(callback);
    setTimeout(() => callback([...mockWindows]), 100);
    return () => {
      mockScheduleWindowsService._listeners =
        mockScheduleWindowsService._listeners.filter(l => l !== callback);
    };
  },
};
